import { useEffect, useMemo, useState, useCallback } from 'react';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import {
  fetchMonthlyIncomeExpenseAccounts,
  fetchMonthlyIncomeExpenseReport
} from '../../../services/reportMonthlyIncomeExpense';
import './MonthlyIncomeExpenseReportPage.css';

const UI_TEXT = {
  'tr-TR': {
    title: 'Aylik Gelir-Gider Raporu',
    description: 'Muhasebe fisi yuklenen donemler icin accounting_voucher_lines tablosundan aylik gelir ve gider analizini uretir.',
    loading: 'Hazirlaniyor...',
    fetchReport: 'Raporu Getir',
    selectCompanyPeriod: 'Lutfen sirket ve donem secin.',
    fetchError: 'Aylik gelir-gider raporu alinamadi.',
    startDate: 'Baslangic Tarihi',
    endDate: 'Bitis Tarihi',
    accountRangeStart: 'Hesap Araligi Baslangic',
    accountRangeEnd: 'Hesap Araligi Bitis',
    allAccounts: 'Tum hesaplar',
    totalIncome: 'Toplam Gelir',
    totalExpense: 'Toplam Gider',
    difference: 'Gelir-Gider Farki',
    colAccountCode: 'Hesap Kodu',
    colAccountName: 'Hesap Aciklamasi',
    colGrandTotal: 'G.Toplam',
    creatingReport: 'Rapor olusturuluyor...',
    emptyReport: 'Raporu getirmek icin filtreleri secip butona basin.',
    incomeSection: 'A-GELIRLER',
    expenseSection: 'B-GIDERLER',
    totalIncomeRow: 'TOPLAM GELIRLER',
    totalExpenseRow: 'TOPLAM GIDERLER',
    diffRow: 'GELIR-GIDER FARKI'
  },
  'en-US': {
    title: 'Monthly Income-Expense Report',
    description: 'Generates monthly income and expense analysis from accounting_voucher_lines for periods with uploaded accounting vouchers.',
    loading: 'Preparing...',
    fetchReport: 'Fetch Report',
    selectCompanyPeriod: 'Please select company and period.',
    fetchError: 'Monthly income-expense report could not be fetched.',
    startDate: 'Start Date',
    endDate: 'End Date',
    accountRangeStart: 'Account Range Start',
    accountRangeEnd: 'Account Range End',
    allAccounts: 'All accounts',
    totalIncome: 'Total Income',
    totalExpense: 'Total Expense',
    difference: 'Income-Expense Difference',
    colAccountCode: 'Account Code',
    colAccountName: 'Account Description',
    colGrandTotal: 'Grand Total',
    creatingReport: 'Building report...',
    emptyReport: 'Select filters and click the button to fetch report.',
    incomeSection: 'A-INCOME',
    expenseSection: 'B-EXPENSES',
    totalIncomeRow: 'TOTAL INCOME',
    totalExpenseRow: 'TOTAL EXPENSES',
    diffRow: 'INCOME-EXPENSE DIFFERENCE'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

function MonthlyIncomeExpenseReportPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const formatMoney = useCallback((value) => {
    const numeric = Number(value || 0);
    return numeric.toLocaleString(localeKey, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [localeKey]);

  const [selection, setSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountRangeStart, setAccountRangeStart] = useState('');
  const [accountRangeEnd, setAccountRangeEnd] = useState('');
  const [accountOptions, setAccountOptions] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [message, setMessage] = useState('');

  const hasSelection = Boolean(selection.companyId && selection.periodId);

  useEffect(() => {
    const loadAccounts = async () => {
      if (!hasSelection) {
        setAccountOptions([]);
        return;
      }
      setLoadingOptions(true);
      try {
        const payload = await fetchMonthlyIncomeExpenseAccounts(selection.companyId, selection.periodId);
        const merged = [...(payload?.incomeAccounts || []), ...(payload?.expenseAccounts || [])];
        const map = new Map();
        merged.forEach((item) => {
          if (!item?.code) return;
          if (!map.has(item.code)) map.set(item.code, item);
        });
        setAccountOptions(Array.from(map.values()));
      } catch {
        setAccountOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    };

    setReport(null);
    setMessage('');
    setAccountRangeStart('');
    setAccountRangeEnd('');
    loadAccounts();
  }, [hasSelection, selection.companyId, selection.periodId]);

  const handleFetch = async () => {
    if (!hasSelection) {
      setMessage(t('selectCompanyPeriod'));
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const payload = await fetchMonthlyIncomeExpenseReport({
        companyId: selection.companyId,
        periodId: selection.periodId,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        accountRangeStart: accountRangeStart || undefined,
        accountRangeEnd: accountRangeEnd || undefined
      });
      setReport(payload);
      setMessage('');
    } catch (error) {
      setReport(null);
      setMessage(error instanceof Error ? error.message : t('fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const monthKeys = useMemo(() => {
    if (!report?.months) return [];
    return report.months.map((item) => ({ key: `${item.year}-${String(item.month).padStart(2, '0')}`, name: item.monthName }));
  }, [report]);

  const monthlyTotalsByName = useMemo(() => {
    const map = new Map();
    (report?.monthlyTotals || []).forEach((item) => map.set(item.monthName, item));
    return map;
  }, [report]);

  const grand = report?.grandTotals || { totalIncome: 0, totalExpenses: 0, totalDifference: 0 };

  return (
    <div className="card monthly-income-expense-card">
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          <button type="button" className="btn-primary" disabled={!hasSelection || loading || loadingOptions} onClick={handleFetch}>
            {loading ? t('loading') : t('fetchReport')}
          </button>
        }
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <section className="monthly-income-expense-filters">
        <label>
          {t('startDate')}
          <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </label>
        <label>
          {t('endDate')}
          <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </label>
        <label>
          {t('accountRangeStart')}
          <select value={accountRangeStart} onChange={(event) => setAccountRangeStart(event.target.value)} disabled={loadingOptions}>
            <option value="">{t('allAccounts')}</option>
            {accountOptions.map((item) => (
              <option key={`start-${item.code}`} value={item.code}>{item.code} - {item.name}</option>
            ))}
          </select>
        </label>
        <label>
          {t('accountRangeEnd')}
          <select value={accountRangeEnd} onChange={(event) => setAccountRangeEnd(event.target.value)} disabled={loadingOptions}>
            <option value="">{t('allAccounts')}</option>
            {accountOptions.map((item) => (
              <option key={`end-${item.code}`} value={item.code}>{item.code} - {item.name}</option>
            ))}
          </select>
        </label>
      </section>

      {message && <p className="monthly-income-expense-message">{message}</p>}

      <section className="monthly-income-expense-summary">
        <article><strong>{formatMoney(grand.totalIncome)}</strong><span>{t('totalIncome')}</span></article>
        <article><strong>{formatMoney(grand.totalExpenses)}</strong><span>{t('totalExpense')}</span></article>
        <article><strong>{formatMoney(grand.totalDifference)}</strong><span>{t('difference')}</span></article>
      </section>

      <div className="monthly-income-expense-table-wrapper">
        <table className="monthly-income-expense-table">
          <thead>
            <tr>
              <th>{t('colAccountCode')}</th>
              <th>{t('colAccountName')}</th>
              {monthKeys.map((month) => (<th key={month.key}>{month.name}</th>))}
              <th>{t('colGrandTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {!report ? (
              <tr>
                <td colSpan={monthKeys.length + 3} className="monthly-income-expense-empty">
                  {loading ? t('creatingReport') : t('emptyReport')}
                </td>
              </tr>
            ) : (
              <>
                <tr className="monthly-income-expense-section monthly-income-expense-section-income"><td colSpan={monthKeys.length + 3}>{t('incomeSection')}</td></tr>
                {(report.incomeAccounts || []).map((row) => (
                  <tr key={`income-${row.accountCode}`}>
                    <td>{row.accountCode}</td>
                    <td>{row.accountName}</td>
                    {monthKeys.map((month) => (
                      <td key={`${row.accountCode}-${month.key}`} className="monthly-income-expense-amount">{formatMoney(row.monthlyData?.[month.key] || 0)}</td>
                    ))}
                    <td className="monthly-income-expense-amount monthly-income-expense-total">{formatMoney(row.total)}</td>
                  </tr>
                ))}
                <tr className="monthly-income-expense-subtotal monthly-income-expense-subtotal-income">
                  <td colSpan={2}>{t('totalIncomeRow')}</td>
                  {monthKeys.map((month) => {
                    const total = monthlyTotalsByName.get(month.name)?.totalIncome || 0;
                    return <td key={`income-total-${month.key}`} className="monthly-income-expense-amount">{formatMoney(total)}</td>;
                  })}
                  <td className="monthly-income-expense-amount">{formatMoney(grand.totalIncome)}</td>
                </tr>

                <tr className="monthly-income-expense-section monthly-income-expense-section-expense"><td colSpan={monthKeys.length + 3}>{t('expenseSection')}</td></tr>
                {(report.expenseAccounts || []).map((row) => (
                  <tr key={`expense-${row.accountCode}`}>
                    <td>{row.accountCode}</td>
                    <td>{row.accountName}</td>
                    {monthKeys.map((month) => (
                      <td key={`${row.accountCode}-${month.key}`} className="monthly-income-expense-amount">{formatMoney(row.monthlyData?.[month.key] || 0)}</td>
                    ))}
                    <td className="monthly-income-expense-amount monthly-income-expense-total">{formatMoney(row.total)}</td>
                  </tr>
                ))}
                <tr className="monthly-income-expense-subtotal monthly-income-expense-subtotal-expense">
                  <td colSpan={2}>{t('totalExpenseRow')}</td>
                  {monthKeys.map((month) => {
                    const total = monthlyTotalsByName.get(month.name)?.totalExpenses || 0;
                    return <td key={`expense-total-${month.key}`} className="monthly-income-expense-amount">{formatMoney(total)}</td>;
                  })}
                  <td className="monthly-income-expense-amount">{formatMoney(grand.totalExpenses)}</td>
                </tr>

                <tr className="monthly-income-expense-subtotal monthly-income-expense-subtotal-diff">
                  <td colSpan={2}>{t('diffRow')}</td>
                  {monthKeys.map((month) => {
                    const total = monthlyTotalsByName.get(month.name)?.difference || 0;
                    return <td key={`diff-total-${month.key}`} className="monthly-income-expense-amount">{formatMoney(total)}</td>;
                  })}
                  <td className="monthly-income-expense-amount">{formatMoney(grand.totalDifference)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MonthlyIncomeExpenseReportPage;
